// Keyboard equivalent of dragging a marker with the mouse.
//
// maplibre-gl 6.4 (#8020) made draggable markers focusable and movable with the arrow keys, but
// `Marker._onKeyDown` returns immediately when the marker is not the default pin, and
// `_updateTabIndex` only manages the default pin's tabindex — custom elements are left to the
// application on purpose. Every marker in this repo is built from our own element
// (`MapInterface.buildMarker`), so none of them got the behaviour. This reimplements it with
// MapLibre's own steps: 1 screen pixel per press, 10 with Shift.

// Screen pixels moved per key press — MapLibre's KEYBOARD_DRAG_SMALL_STEP / _LARGE_STEP.
export const KEYBOARD_DRAG_STEP = 1;
export const KEYBOARD_DRAG_LARGE_STEP = 10;

export const ARROW_KEY_DELTAS = {
    ArrowUp:    [0, -1],
    ArrowDown:  [0, 1],
    ArrowLeft:  [-1, 0],
    ArrowRight: [1, 0]
};

/**
 * Makes an already-draggable Marker movable with the keyboard.
 *
 * `dragend` is what consumers hang their "confirm the move?" step on (see PlanMap), so it fires
 * ONCE — when focus leaves the marker, or when the move is validated with Enter. MapLibre fires it
 * on every keyup for its default pin, which here would open one modal per arrow press.
 *
 * @param map - the MapLibre map the marker belongs to
 * @param marker - the MapLibre Marker, already created with `draggable: true`
 * @param markerElement - the marker's custom DOM element
 * @param ariaLabel - accessible name for the element
 */
export const makeMarkerKeyboardDraggable = (map, marker, markerElement, ariaLabel) => {
    if (!map || !marker || !markerElement) {
        return;
    }

    markerElement.setAttribute('tabindex', '0');
    markerElement.setAttribute('role', 'button');

    if (ariaLabel) {
        markerElement.setAttribute('aria-label', ariaLabel);
    }

    let isDragging = false;

    const endDrag = () => {
        if (!isDragging) {
            return;
        }

        isDragging = false;
        marker.fire('dragend');
    };

    markerElement.addEventListener('keydown', (event) => {
        // Space is the other activation key role="button" promises. Left unhandled it scrolled the
        // page and toggled the popup, so it commits the move like Enter.
        if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
            // preventDefault cancels the `keypress` that would follow, and with it the listener
            // Marker.setPopup installs (Space/Enter -> togglePopup). Both our draggable markers
            // carry a popup (planMap, ridePoisMap), so without this Enter would commit the move AND
            // open the popup — and that popup, opened without the element's `click` handler, never
            // lands in the map's openPopups list.
            event.preventDefault();
            event.stopPropagation();
            endDrag();

            return;
        }

        const delta = ARROW_KEY_DELTAS[event.key];

        if (!delta || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        // preventDefault keeps the arrows from scrolling the page. stopPropagation is what keeps
        // them from ALSO driving the map: MapLibre's HandlerManager listens for keydown on
        // getCanvasContainer(), bubbling and without filtering the target, and Marker.addTo appends
        // the marker element inside that very container — so an arrow would pan the map 100px and
        // Shift+arrow would rotate the bearing 15° on top of moving the marker. MapLibre's own
        // default-pin handler calls both for the same reason.
        event.preventDefault();
        event.stopPropagation();

        const step = event.shiftKey ? KEYBOARD_DRAG_LARGE_STEP : KEYBOARD_DRAG_STEP;
        const position = map.project(marker.getLngLat());

        marker.setLngLat(map.unproject([
            position.x + delta[0] * step,
            position.y + delta[1] * step
        ]));

        if (!isDragging) {
            isDragging = true;
            marker.fire('dragstart');
        }

        marker.fire('drag');
    });

    markerElement.addEventListener('blur', endDrag);
};
